/**
 * eSocial Types for MEV-Med System
 */

// ==================== ENUMS ====================

/**
 * eSocial Event Types
 */
export enum ESocialEventType {
  S_1000 = 'S_1000',  // Informações do Empregador
  S_2200 = 'S_2200',  // Admissao do Trabalhador
  S_2205 = 'S_2205',  // Alteracao de Dados Cadastrais
  S_2210 = 'S_2210',  // CAT - Comunicação de Acidente
  S_2220 = 'S_2220',  // Monitoramento da Saude (ASO)
  S_2221 = 'S_2221',  // Exame Toxicologico
  S_2230 = 'S_2230',  // Afastamento Temporário
  S_2240 = 'S_2240'   // Condicoes Ambientais do Trabalho
}

/**
 * eSocial Event Status
 */
export enum ESocialEventStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  SUBMITTED = 'SUBMITTED',
  AUTHORIZED = 'AUTHORIZED',
  REJECTED = 'REJECTED',
  ERROR = 'ERROR',
  CANCELLED = 'CANCELLED'
}

/**
 * eSocial Log Action Types
 */
export enum ESocialLogAction {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  SUBMITTED = 'SUBMITTED',
  RESPONSE_RECEIVED = 'RESPONSE_RECEIVED',
  RESUBMITTED = 'RESUBMITTED',
  CANCELLED = 'CANCELLED',
  STATUS_CHANGED = 'STATUS_CHANGED'
}

// ==================== INTERFACES ====================

/**
 * eSocial Event interface
 */
export interface ESocialEvent {
  id: number;
  companyId: number;
  employeeId?: number;
  eventType: ESocialEventType;
  eventStatus: ESocialEventStatus;
  receiptNumber?: string;
  protocolNumber?: string;
  eventDate: string;
  dueDate?: string;
  submissionDate?: string;
  responseDate?: string;
  xmlContent?: string;
  responseXml?: string;
  errorCode?: string;
  errorMessage?: string;
  metadata?: Record<string, unknown>;
  /** Quando a ficha foi alterada à mão pela última vez; null se nunca foi. */
  metadataEditedAt?: string | null;
  notes?: string;
  createdBy?: number;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  // Relationships
  employee?: {
    id: number;
    name: string;
    cpf?: string;
  };
  creator?: {
    id: number;
    name: string;
    email?: string;
  };
}

/**
 * eSocial Event Log interface
 */
export interface ESocialEventLog {
  id: number;
  eventId: number;
  action: ESocialLogAction;
  previousStatus?: string;
  newStatus?: string;
  requestXml?: string;
  responseXml?: string;
  responseCode?: string;
  responseMessage?: string;
  userId?: number;
  createdAt: string;
  // Relationships
  user?: {
    id: number;
    name: string;
    email?: string;
  };
}

/**
 * eSocial Dashboard interface
 */
export interface ESocialDashboard {
  stats: {
    total: number;
    draft: number;
    pending: number;
    submitted: number;
    authorized: number;
    rejected: number;
    error: number;
    cancelled: number;
  };
  recentEvents: ESocialEvent[];
  upcomingDueDates: ESocialEvent[];
}

/**
 * eSocial Event Filters interface
 */
export interface ESocialEventFilters {
  eventType?: ESocialEventType | ESocialEventType[];
  eventStatus?: ESocialEventStatus | ESocialEventStatus[];
  employeeId?: number;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

/**
 * Create eSocial Event Request
 */
export interface CreateESocialEventRequest {
  eventType: ESocialEventType;
  eventDate: string;
  employeeId?: number;
  dueDate?: string;
  metadata?: Record<string, unknown>;
  notes?: string;
}

/**
 * Update eSocial Event Request
 */
export interface UpdateESocialEventRequest {
  eventType?: ESocialEventType;
  eventDate?: string;
  employeeId?: number;
  dueDate?: string;
  metadata?: Record<string, unknown>;
  notes?: string;
}

/**
 * eSocial Event Form Data
 */
export interface ESocialEventFormData {
  eventType: ESocialEventType | '';
  eventDate: string;
  employeeId: string;
  dueDate: string;
  notes: string;
}

// ==================== LABELS ====================

/**
 * Event Type Labels (Portuguese)
 */
export const ESOCIAL_EVENT_TYPE_LABELS: Record<ESocialEventType, string> = {
  [ESocialEventType.S_1000]: 'S-1000 - Informações do Empregador',
  [ESocialEventType.S_2200]: 'S-2200 - Admissao do Trabalhador',
  [ESocialEventType.S_2205]: 'S-2205 - Alteracao de Dados Cadastrais',
  [ESocialEventType.S_2210]: 'S-2210 - CAT - Comunicação de Acidente',
  [ESocialEventType.S_2220]: 'S-2220 - Monitoramento da Saude (ASO)',
  [ESocialEventType.S_2221]: 'S-2221 - Exame Toxicologico',
  [ESocialEventType.S_2230]: 'S-2230 - Afastamento Temporário',
  [ESocialEventType.S_2240]: 'S-2240 - Condicoes Ambientais do Trabalho'
};

/**
 * Short Event Type Labels (Portuguese)
 */
export const ESOCIAL_EVENT_TYPE_SHORT_LABELS: Record<ESocialEventType, string> = {
  [ESocialEventType.S_1000]: 'S-1000',
  [ESocialEventType.S_2200]: 'S-2200',
  [ESocialEventType.S_2205]: 'S-2205',
  [ESocialEventType.S_2210]: 'S-2210',
  [ESocialEventType.S_2220]: 'S-2220',
  [ESocialEventType.S_2221]: 'S-2221',
  [ESocialEventType.S_2230]: 'S-2230',
  [ESocialEventType.S_2240]: 'S-2240'
};

/**
 * Event Status Labels (Portuguese)
 */
export const ESOCIAL_STATUS_LABELS: Record<ESocialEventStatus, string> = {
  [ESocialEventStatus.DRAFT]: 'Rascunho',
  [ESocialEventStatus.PENDING]: 'Pendente',
  [ESocialEventStatus.SUBMITTED]: 'Transmitido',
  [ESocialEventStatus.AUTHORIZED]: 'Autorizado',
  [ESocialEventStatus.REJECTED]: 'Rejeitado',
  [ESocialEventStatus.ERROR]: 'Erro',
  [ESocialEventStatus.CANCELLED]: 'Cancelado'
};

/**
 * Log Action Labels (Portuguese)
 */
export const ESOCIAL_LOG_ACTION_LABELS: Record<ESocialLogAction, string> = {
  [ESocialLogAction.CREATED]: 'Criado',
  [ESocialLogAction.UPDATED]: 'Atualizado',
  [ESocialLogAction.SUBMITTED]: 'Transmitido',
  [ESocialLogAction.RESPONSE_RECEIVED]: 'Resposta Recebida',
  [ESocialLogAction.RESUBMITTED]: 'Retransmitido',
  [ESocialLogAction.CANCELLED]: 'Cancelado',
  [ESocialLogAction.STATUS_CHANGED]: 'Status Alterado'
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Get status color for chips
 */
export const getEventStatusColor = (
  status: ESocialEventStatus
): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
  switch (status) {
    case ESocialEventStatus.DRAFT:
      return 'default';
    case ESocialEventStatus.PENDING:
      return 'warning';
    case ESocialEventStatus.SUBMITTED:
      return 'info';
    case ESocialEventStatus.AUTHORIZED:
      return 'success';
    case ESocialEventStatus.REJECTED:
      return 'error';
    case ESocialEventStatus.ERROR:
      return 'error';
    case ESocialEventStatus.CANCELLED:
      return 'default';
    default:
      return 'default';
  }
};

/**
 * Get log action color for chips
 */
export const getLogActionColor = (
  action: ESocialLogAction
): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
  switch (action) {
    case ESocialLogAction.CREATED:
      return 'success';
    case ESocialLogAction.UPDATED:
      return 'info';
    case ESocialLogAction.SUBMITTED:
      return 'primary';
    case ESocialLogAction.RESPONSE_RECEIVED:
      return 'info';
    case ESocialLogAction.RESUBMITTED:
      return 'warning';
    case ESocialLogAction.CANCELLED:
      return 'error';
    case ESocialLogAction.STATUS_CHANGED:
      return 'secondary';
    default:
      return 'default';
  }
};

/**
 * Check if event can be edited
 */
export const canEventBeEdited = (status: ESocialEventStatus): boolean => {
  return status === ESocialEventStatus.DRAFT || status === ESocialEventStatus.REJECTED;
};

/**
 * Check if event can be submitted
 */
export const canEventBeSubmitted = (status: ESocialEventStatus): boolean => {
  return status === ESocialEventStatus.DRAFT ||
         status === ESocialEventStatus.PENDING ||
         status === ESocialEventStatus.REJECTED ||
         status === ESocialEventStatus.ERROR;
};

/**
 * Check if event can be cancelled
 */
export const canEventBeCancelled = (status: ESocialEventStatus): boolean => {
  return status === ESocialEventStatus.DRAFT ||
         status === ESocialEventStatus.PENDING;
};

/**
 * Check if event can be deleted
 */
export const canEventBeDeleted = (status: ESocialEventStatus): boolean => {
  return status === ESocialEventStatus.DRAFT;
};

/**
 * Check if event type requires an employee
 */
export const eventTypeRequiresEmployee = (eventType: ESocialEventType): boolean => {
  const employeeRequiredTypes = [
    ESocialEventType.S_2200,
    ESocialEventType.S_2205,
    ESocialEventType.S_2210,
    ESocialEventType.S_2220,
    ESocialEventType.S_2221,
    ESocialEventType.S_2230,
    ESocialEventType.S_2240
  ];
  return employeeRequiredTypes.includes(eventType);
};
